import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, SquareArrowOutUpRight } from 'lucide-react';

export default function AuthGatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');
  const isVerified = searchParams.get('verified') === 'true';
  
  // Default to signup if user has chain data (coming from chain creation)
  const hasChainData = sessionStorage.getItem('chainDedication');
  const [authMode, setAuthMode] = useState(hasChainData ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpStep, setIsOtpStep] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to format errors in a user-friendly way
  const formatError = (error) => {
    // Handle network errors
    if (error.message === 'Failed to fetch') {
      return 'Unable to connect. Please check your internet connection and try again.';
    }
    
    // Handle timeout errors
    if (error.message.includes('timeout') || error.message.includes('504')) {
      return 'The request timed out. Please try again in a moment.';
    }
    
    // Handle JSON parse errors (like the one you experienced)
    if (error.message.includes('JSON') || error.message.includes('Unexpected token')) {
      return 'We encountered a technical issue. Please try again.';
    }
    
    // Handle common auth errors with friendly messages
    const friendlyMessages = {
      'Invalid login credentials': 'Email or password is incorrect. Please try again.',
      'User already registered': 'This email is already registered. Please sign in instead.',
      'Email not confirmed': 'Please check your email and click the verification link.',
      'Invalid email': 'Please enter a valid email address.',
      'Weak password': 'Password must be at least 8 characters long.',
    };
    
    // Check if error message matches any known patterns
    for (const [pattern, friendly] of Object.entries(friendlyMessages)) {
      if (error.message.includes(pattern)) {
        return friendly;
      }
    }
    
    // For any other errors, return a generic friendly message
    if (error.message && error.message.length > 100) {
      return 'Something went wrong. Please try again or contact support if the issue persists.';
    }
    
    return error.message || 'An unexpected error occurred. Please try again.';
  };

  // Auto-verify when returning from email confirmation
  useEffect(() => {
    if (isVerified && !isOtpStep) {
      // Check if we have prayer chain data in sessionStorage
      const hasChainData = sessionStorage.getItem('chainDedication');
      
      if (hasChainData) {
        // User came back from email verification with chain data
        const storedEmail = sessionStorage.getItem('signupEmail');
        const storedPassword = sessionStorage.getItem('signupPassword');
        
        if (storedEmail && storedPassword) {
          setEmail(storedEmail);
          setPassword(storedPassword);
          // Auto-trigger verification and chain creation
          autoVerifyAndCreateChain(storedEmail, storedPassword);
        }
      }
    }
  }, [isVerified]);

  const autoVerifyAndCreateChain = async (userEmail, userPassword) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: userEmail,
          password: userPassword
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Login failed');
      }

      // Store auth token
      localStorage.setItem('auth_token', result.token);

      // Clear signup credentials
      sessionStorage.removeItem('signupEmail');
      sessionStorage.removeItem('signupPassword');

      // Create chain with authenticated user
      await createChainAndRedirect(result.token);
      
    } catch (err) {
      setError(formatError(err));
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      // Handle non-JSON responses
      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        throw new Error('Unable to process the response. Please try again.');
      }

      if (!response.ok) {
        throw new Error(result.error || 'Sign in failed');
      }

      // Store auth token
      localStorage.setItem('auth_token', result.token);

      // Create chain with authenticated user
      await createChainAndRedirect(result.token);
      
    } catch (err) {
      setError(formatError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUpSendOtp = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email,
          password: password 
        })
      });

      // Handle non-JSON responses
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error('Unable to process the response. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create account');
      }

      // Store email and password temporarily for auto-verification
      sessionStorage.setItem('signupEmail', email);
      sessionStorage.setItem('signupPassword', password);

      // Show email verification message
      setIsOtpStep(true);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Import Supabase client dynamically
      const { createClient } = await import('@supabase/supabase-js');
      const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://xvhellooihpelwifxuik.supabase.co';
      const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inh2aGVsbG9vaWhwZWx3aWZ4dWlrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzIwNzI1MTAsImV4cCI6MjA0NzY0ODUxMH0.nAG2sveIe3RUP9XLgY4wj0VfyWdBGN5_oqzzBWM-_1Y';
      const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: email,
        token: otp,
        type: 'signup'
      });

      if (verifyError) throw verifyError;

      // Now sign in with the verified credentials
      const response = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email,
          password: password
        })
      });

      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        throw new Error('Unable to process the response. Please try again.');
      }

      if (!response.ok) {
        throw new Error(result.message || 'Login failed');
      }

      // Store auth token
      localStorage.setItem('auth_token', result.token);

      // Clear signup credentials
      sessionStorage.removeItem('signupEmail');
      sessionStorage.removeItem('signupPassword');

      // Create chain with authenticated user
      await createChainAndRedirect(result.token);
      
    } catch (err) {
      setError(formatError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const createChainAndRedirect = async (token) => {
    // Check if there's pending chain data to create
    const chainDedication = sessionStorage.getItem('chainDedication');
    const chainType = sessionStorage.getItem('chainType');
    const whoData = sessionStorage.getItem('whoIsThisForData');
    const storyData = sessionStorage.getItem('storyData');
    
    if (chainDedication && chainType && whoData && storyData) {
      // Parse the stored data
      const whoDataParsed = JSON.parse(whoData);
      const storyDataParsed = JSON.parse(storyData);

      // Create the chain with authenticated user
      const chainResponse = await fetch('/api/chains/create', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          chainType: chainType,
          dedication: chainDedication,
          recipientName: whoDataParsed.names[0],
          additionalNames: whoDataParsed.names.slice(1),
          recipientImage: whoDataParsed.imagePreviewUrl || null,
          tags: storyDataParsed.tag ? [storyDataParsed.tag] : [],
          description: storyDataParsed.story || null,
          isPublic: storyDataParsed.isPublic
        })
      });

      const chainResult = await chainResponse.json();

      if (!chainResponse.ok) {
        throw new Error(chainResult.error || 'Failed to create chain');
      }

      // Clear all session storage
      sessionStorage.removeItem('chainDedication');
      sessionStorage.removeItem('chainType');
      sessionStorage.removeItem('whoIsThisForData');
      sessionStorage.removeItem('storyData');

      // Redirect to server-side details page (environment-aware)
      const baseUrl = process.env.NODE_ENV === 'production' 
        ? 'https://yesod.us' 
        : 'http://localhost:5001';
      window.location.href = `${baseUrl}/${chainResult.data.shortCode}/details`;
    } else if (redirectPath) {
      navigate(redirectPath);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <img 
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png" 
            alt="Yesod" 
            className="h-10"
          />
          <h1 className="text-xl font-semibold text-gray-900">Yesod</h1>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#667eea] rounded-lg hover:bg-[#5568d3] transition-all duration-200">
          Continue in App
          <SquareArrowOutUpRight size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          {!isOtpStep && (
            <div className="text-center mb-8">
              <img 
                src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png" 
                alt="Yesod" 
                className="h-24 w-auto mx-auto mb-8"
              />
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {authMode === 'signin' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-base text-gray-600">
                Sign in to activate your prayer chain
              </p>
            </div>
          )}

          {!isOtpStep && (
            <>
              {/* Auth Mode Toggle */}
              <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-lg">
                <button
                  onClick={() => setAuthMode('signin')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${
                    authMode === 'signin'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setAuthMode('signup')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${
                    authMode === 'signup'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Sign Up
                </button>
              </div>

              {/* Email Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#667eea] focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)] transition-all"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#667eea] focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)] transition-all"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={authMode === 'signin' ? handleSignIn : handleSignUpSendOtp}
                disabled={isLoading || !email || !password}
                className="w-full py-3 text-base font-semibold text-white bg-gradient-to-br from-[#667eea] to-[#764ba2] rounded-xl hover:translate-y-[-2px] hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isLoading ? 'Loading...' : authMode === 'signin' ? 'Sign In' : 'Continue'}
              </button>
            </>
          )}

          {isOtpStep && (
            <>
              {/* Email Verification Icon and Message */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-100 to-green-50 rounded-full mb-6">
                  <Mail className="text-green-600" size={48} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Email Verification</h3>
                <p className="text-base text-gray-600">
                  Enter the code sent to
                </p>
                <p className="text-base font-semibold text-gray-900 mt-1">{email}</p>
              </div>

              {/* Individual Digit Inputs */}
              <div className="mb-6">
                <div className="flex justify-center gap-3 mb-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength={1}
                      value={otp[index] || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        const newOtp = otp.split('');
                        newOtp[index] = value;
                        setOtp(newOtp.join(''));
                        // Auto-focus next input if value entered
                        if (index < 5 && value) {
                          const nextInput = e.target.parentElement.children[index + 1];
                          if (nextInput) nextInput.focus();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace') {
                          if (!otp[index] && index > 0) {
                            // Move to previous input if current is empty
                            const prevInput = e.target.parentElement.children[index - 1];
                            if (prevInput) prevInput.focus();
                          } else {
                            // Clear current input
                            const newOtp = otp.split('');
                            newOtp[index] = '';
                            setOtp(newOtp.join(''));
                          }
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                        if (pastedData) {
                          setOtp(pastedData);
                          // Focus last filled input or last input
                          const lastIndex = Math.min(pastedData.length - 1, 5);
                          const lastInput = e.target.parentElement.children[lastIndex];
                          if (lastInput) lastInput.focus();
                        }
                      }}
                      autoFocus={index === 0}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#667eea] focus:ring-2 focus:ring-[#667eea]/20 transition-all"
                      style={{ fontFamily: 'monospace' }}
                    />
                  ))}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-center">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {/* Verify Button */}
              <button
                onClick={handleVerifyOtp}
                disabled={isLoading || otp.length !== 6}
                className="w-full py-3 text-base font-semibold text-white bg-gradient-to-br from-[#667eea] to-[#764ba2] rounded-xl hover:translate-y-[-2px] hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 mb-3"
              >
                {isLoading ? 'Verifying...' : 'Verify Email'}
              </button>

              {/* Resend and Back Links */}
              <div className="text-center">
                <p className="text-sm text-gray-500">
                  Didn't receive the code?{' '}
                  <button
                    onClick={handleSignUpSendOtp}
                    disabled={isLoading}
                    className="text-[#667eea] font-semibold hover:text-[#5568d3] transition-colors"
                  >
                    Resend Code
                  </button>
                </p>
                <button
                  onClick={() => {
                    setIsOtpStep(false);
                    setOtp('');
                    setError(null);
                  }}
                  className="mt-3 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  ← Back to sign up
                </button>
              </div>
            </>
          )}

          {/* Error Message - only shown when NOT in OTP step */}
          {error && !isOtpStep && (
            <div className="mt-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-800 text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
